// MIDAS V3 — Rail row dropdown menu (Rename / Delete)
//
// Replaces the previous `v2/ui/dropdown-menu` usage in `MidasHistoryRailV3`'s
// `RailRow`. The shadcn `DropdownMenuContent` from v2 portals to
// `document.body` and applies its background/text via the Tailwind tokens
// `bg-popover` / `text-popover-foreground` / `border`. Those custom-property
// pipelines are defined globally for the shadcn V2 dock surface — they do NOT
// inherit the V3 token sheet, which is scoped to `.midas-v3-root`. Result on
// the V3 history rail: the menu portals OUT of `.midas-v3-root` and renders
// with transparent background, no border, browser-default text contrast — the
// rail rows bleed through behind it. See screenshot `too_transparent.PNG`.
//
// Fix strategy: portal the menu to `document.body` ourselves but wrap it in a
// `<div className="midas-v3-root p-{theme}">` so the V3 custom properties
// resolve inside. Same architectural pattern as `NewChatDialogV3` (which sets
// `background: transparent` on the wrapper so the cream `--midas-v3-bg` paint
// on `.midas-v3-root` doesn't tint the entire viewport).
//
// We don't use Radix's `DropdownMenu` at all here because shadcn's wrapper
// hides `Portal` inside `Content` and doesn't surface a `container` prop. A
// minimal trigger-anchored popper with manual outside-click + Esc handling
// keeps V2 untouched and removes a hidden surface for portal-scope bugs.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Lucide icons only.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Check, Columns2, Pencil, Trash2 } from "lucide-react";

import { resolveV3PaletteClass } from "../../midasV3Palette";

// ============================================================
// Public props
// ============================================================

export interface RailRowMenuV3Item {
  /** Stable key for React lists and the rare programmatic select. */
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Visual + semantic destructive treatment (red icon/text + red-tint hover). */
  destructive?: boolean;
  /** MODEL-PICKER P3 ("Attach chat" at the 2-column cap): visible but
   *  disabled — faint text/icon, no hover fill, default cursor, no onSelect.
   *  Additive prop; existing callers unaffected. */
  disabled?: boolean;
  /** Ink-chip tooltip shown while hovering a DISABLED item (design: anchored
   *  below the item so it stays inside the rail). Only rendered when
   *  `disabled` is true. */
  disabledTooltip?: string;
  /** MIDAS-V3-PALETTES-TEAL-SLATE: when DEFINED, the row is a radio item
   *  (`role="menuitemradio"` + `aria-checked`) with a trailing check mark.
   *  The 14px mark slot is always reserved on such rows so selecting a
   *  different option never shifts the row width/height. Additive prop —
   *  callers that omit it (Rename / Delete / Attach chat) render the exact
   *  same DOM and roles as before. */
  checked?: boolean;
  onSelect: () => void;
}

export interface RailRowMenuV3Props {
  open: boolean;
  onClose: () => void;
  /** The trigger element. The menu anchors to its right edge by default. */
  triggerRef: React.RefObject<HTMLElement>;
  items: RailRowMenuV3Item[];
  ariaLabel?: string;
}

// Theme: the portal wrapper reads the live V3 root's palette class via the
// shared `resolveV3PaletteClass()` helper (midasV3Palette.ts). It replaced a
// local 2-way DOM sniff that fell through to `p-machineid` for any palette
// other than claude — which painted this menu brand-blue on `p-teal` /
// `p-slate` pages (MIDAS-V3-PALETTES-TEAL-SLATE).

// ============================================================
// Component
// ============================================================

const MENU_OFFSET_Y = 4; // gap between trigger bottom and menu top
const MENU_MIN_WIDTH = 168;
const VIEWPORT_MARGIN = 8; // clamp the menu inside the viewport with this padding

const RailRowMenuV3: React.FC<RailRowMenuV3Props> = ({
  open,
  onClose,
  triggerRef,
  items,
  ariaLabel,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    minWidth: number;
  } | null>(null);

  // Capture the active element when opening so we can restore focus on close.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // ── Position the menu against the trigger ───────────────────
  // Anchored to the trigger button's bottom-right corner. Clamp inside the
  // viewport so a row near the bottom of the rail never clips. We measure on
  // open and again on resize/scroll. (The history rail itself is the scroll
  // container; the menu lives in document.body via portal, so any scroll
  // inside the rail moves the trigger but not the menu — we recompute.)
  const recomputePosition = useCallback(() => {
    const trig = triggerRef.current;
    const menuEl = menuRef.current;
    if (!trig || !menuEl) return;
    const tr = trig.getBoundingClientRect();
    const mw = Math.max(menuEl.offsetWidth, MENU_MIN_WIDTH);
    const mh = menuEl.offsetHeight;
    // Default: open below + right-aligned to the trigger's right edge.
    let left = tr.right - mw;
    let top = tr.bottom + MENU_OFFSET_Y;
    // Right-edge clamp.
    if (left + mw > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - VIEWPORT_MARGIN - mw;
    }
    // Left-edge clamp.
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    // Bottom-edge clamp — flip above the trigger if there's no room.
    if (top + mh > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, tr.top - MENU_OFFSET_Y - mh);
    }
    setPosition({ top, left, minWidth: mw });
  }, [triggerRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    // First-frame measure: render once at offscreen origin so we can measure
    // the actual width/height, then position. We do this by setting position
    // immediately on the next frame.
    recomputePosition();
    const onWinChange = () => recomputePosition();
    window.addEventListener("resize", onWinChange);
    window.addEventListener("scroll", onWinChange, true);
    return () => {
      window.removeEventListener("resize", onWinChange);
      window.removeEventListener("scroll", onWinChange, true);
    };
  }, [open, recomputePosition]);

  // ── Open/close side effects ─────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    // Cache focus restoration target.
    restoreFocusRef.current =
      (document.activeElement as HTMLElement | null) || null;

    // Focus the menu container so keyboard navigation lands here.
    const id = window.setTimeout(() => {
      menuRef.current?.focus();
    }, 0);

    // Esc + outside-click handlers.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    const onDocMouseDown = (e: MouseEvent) => {
      const m = menuRef.current;
      const trig = triggerRef.current;
      const target = e.target as Node | null;
      if (!target) return;
      if (m && m.contains(target)) return;
      if (trig && trig.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open, onClose, triggerRef]);

  // Restore focus on close.
  useEffect(() => {
    if (open) return;
    const el = restoreFocusRef.current;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
    restoreFocusRef.current = null;
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const theme = resolveV3PaletteClass();

  // Initial render before measurement: keep the menu offscreen so the user
  // never sees a flash at top-left. Measurement happens in the same paint
  // (useLayoutEffect) so this is invisible in practice.
  const initialOffscreen: React.CSSProperties = {
    top: -9999,
    left: -9999,
    minWidth: MENU_MIN_WIDTH,
  };
  const placed: React.CSSProperties = position
    ? {
        top: position.top,
        left: position.left,
        minWidth: position.minWidth,
      }
    : initialOffscreen;

  const node = (
    // Wrapper carries the V3 root class so `var(--midas-v3-*)` resolve inside
    // the portal. `background: transparent` defeats `.midas-v3-root`'s
    // `background: var(--midas-v3-bg)` rule which would otherwise paint the
    // whole document body cream (same trap NewChatDialogV3 documents at lines
    // 547-552).
    <div
      className={`midas-v3-root ${theme}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        background: "transparent",
        // Don't capture clicks on the empty wrapper — outside-click is handled
        // via the document-level mousedown listener, but we still want the
        // page underneath to receive hover/scroll signals.
        pointerEvents: "none",
      }}
      role="presentation"
    >
      <div
        ref={menuRef}
        role="menu"
        tabIndex={-1}
        aria-label={ariaLabel}
        style={{
          position: "fixed",
          ...placed,
          background: "var(--midas-v3-card)",
          color: "var(--midas-v3-ink)",
          border: "1px solid var(--midas-v3-border)",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          padding: 6,
          fontFamily: "var(--font-sans), Sora, system-ui, sans-serif",
          fontSize: 13,
          // Re-enable pointer events for the menu itself.
          pointerEvents: "auto",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {items.map((item) => (
          <RailRowMenuV3ItemButton
            key={item.id}
            item={item}
            onAfterSelect={onClose}
          />
        ))}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

// ============================================================
// Item button — keeps hover/destructive styling state-local
// ============================================================

const RailRowMenuV3ItemButton: React.FC<{
  item: RailRowMenuV3Item;
  onAfterSelect: () => void;
}> = ({ item, onAfterSelect }) => {
  const [hovered, setHovered] = useState(false);
  const { destructive, disabled } = item;

  // Hover background: accent-soft tint for the standard action; red tint for
  // destructive. Both use `color-mix` so they look correct on either palette.
  // Disabled items keep a transparent background (no hover fill).
  const hoverBg = disabled
    ? "transparent"
    : destructive
    ? "color-mix(in srgb, #dc2626 12%, transparent)"
    : "var(--midas-v3-accent-soft)";

  // Idle icon color: muted/faint. On hover (or destructive at rest): tinted.
  // Disabled: faint text + icon (design: cap state of "Attach chat").
  const idleIconColor = disabled
    ? "var(--midas-v3-faint)"
    : destructive
    ? "#dc2626"
    : "var(--midas-v3-faint)";
  const hoverIconColor = disabled
    ? "var(--midas-v3-faint)"
    : destructive
    ? "#dc2626"
    : "var(--midas-v3-ink)";
  // Idle text color: ink-2 (slightly softer than full ink) for visual hierarchy.
  // Destructive items keep red text at rest so the action is clearly flagged.
  const idleTextColor = disabled
    ? "var(--midas-v3-faint)"
    : destructive
    ? "#dc2626"
    : "var(--midas-v3-ink)";
  const hoverTextColor = disabled
    ? "var(--midas-v3-faint)"
    : destructive
    ? "#dc2626"
    : "var(--midas-v3-ink)";

  const showTooltip = !!item.disabledTooltip && disabled && hovered;

  return (
    <button
      type="button"
      // A row with `checked` DEFINED is one option of a radio group (the
      // palette picker); otherwise it stays a plain action item. Callers that
      // never set `checked` keep the exact role/ARIA they had before.
      role={item.checked === undefined ? "menuitem" : "menuitemradio"}
      aria-checked={item.checked === undefined ? undefined : item.checked}
      aria-disabled={disabled || undefined}
      onClick={
        disabled
          ? undefined
          : () => {
              item.onSelect();
              onAfterSelect();
            }
      }
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 6,
        border: "none",
        background: hovered ? hoverBg : "transparent",
        color: hovered ? hoverTextColor : idleTextColor,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        textAlign: "left",
        width: "100%",
        outline: "none",
        position: "relative",
        transition: "background-color 0.12s ease, color 0.12s ease",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 14,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: hovered ? hoverIconColor : idleIconColor,
          flexShrink: 0,
          transition: "color 0.12s ease",
        }}
      >
        {item.icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
      {/* Radio rows reserve a fixed 14px trailing slot for the check mark so
          the row geometry is identical whether or not it is the active one
          (`aria-checked` above carries the state for assistive tech). */}
      {item.checked !== undefined && (
        <span
          aria-hidden="true"
          style={{
            width: 14,
            height: 14,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--midas-v3-accent)",
            flexShrink: 0,
          }}
        >
          {item.checked ? <Check size={14} strokeWidth={2} /> : null}
        </span>
      )}
      {/* Ink-chip tooltip for disabled items (design: mono 10.5, --ink bg,
          --bg text, radius 4, anchored BELOW the item so it stays inside
          the rail). */}
      {showTooltip && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            left: 12,
            top: "calc(100% + 3px)",
            zIndex: 60,
            background: "var(--midas-v3-ink)",
            color: "var(--midas-v3-bg)",
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.03em",
            padding: "3px 8px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {item.disabledTooltip}
        </span>
      )}
    </button>
  );
};

// ============================================================
// Convenience icon exports — keeps callers from importing lucide
// just to pass a `Pencil`/`Trash2`.
// ============================================================

export const RailRowMenuV3RenameIcon: React.FC = () => (
  <Pencil size={14} strokeWidth={1.75} />
);

export const RailRowMenuV3DeleteIcon: React.FC = () => (
  <Trash2 size={14} strokeWidth={1.75} />
);

/** MODEL-PICKER P3: two-columns glyph for "Attach chat" / "Detach chat" and
 *  the attached-row cue in the rail (spec §6 — lucide `Columns2`). */
export const RailRowMenuV3AttachIcon: React.FC = () => (
  <Columns2 size={14} strokeWidth={1.75} />
);

export default RailRowMenuV3;
